<template>
	<div class="editing-view">
		<div class="edit-container">
			<!-- Left Column: Chat Interface -->
			<div class="chat-column">
				<div class="chat-header" @click="closeEditor">
					<button class="back-btn">
						<AppChevronIcon direction="left" color="var(--txt-0)" :size="12" />
					</button>
					<p class="chat-title">{{ currentNewsletter?.subject ? currentNewsletter?.subject : getString('new_newsletter') }}</p>
				</div>

				<div class="chat-messages" ref="chatMessages">
					<div class="messages-list">
						<AppLoader v-if="newsletterStore.loadingConversation" class="messages-loader"/>
						<div v-else-if="newsletterStore.conversationMessages.length === 0" class="no-messages">
							<p>{{ getString('no_messages') }}</p>
						</div>
						<div v-else v-for="(message, index) in newsletterStore.conversationMessages" :key="index" class="message" :class="getMessageClass(message)">
							<div class="message-content">
								<!-- Image generation system message -->
								<div v-if="message.role === 'system' && message.type === 'newsletter_image'" class="image-generation-label">
									<AppLoader v-if="hasGeneratingImages(message)" :size="16" />
									<span>{{ getImageGenerationLabel(message) }}</span>
									<span v-if="message.total_charged_amount" class="image-cost">
										• {{ Math.abs(message.total_charged_amount) }} {{ Math.abs(message.total_charged_amount) === 1 ? getString('credit') : getString('credits') }}
									</span>
								</div>
								<!-- Normal message content -->
								<div v-if="message.content && message.role === 'assistant'" class="ai-message-wrapper">
									<pre class="ai-response">{{ message.content }}</pre>
									<div v-if="message.status === 'completed'" class="ai-message-meta">
										<p class="meta-duration">{{ message.created_at && message.finalized_at ? formatDuration(message.created_at, message.finalized_at) : '' }}</p>
										<p class="meta-cost">{{ message.charged_amount !== null && message.charged_amount !== undefined ? Math.abs(message.charged_amount) + ' ' + (Math.abs(message.charged_amount) === 1 ? getString('credit') : getString('credits')) : '' }}</p>
									</div>
								</div>
								<!-- Rollback system message -->
								<div v-else-if="message.role === 'user' && message.type === 'newsletter_rollback'" class="rollback-label">
									{{ getString('changes_reverted') }}
								</div>
								<div v-else-if="message.content">
									<p>{{ message.content }}</p>
									<!-- Attachments indicator for user messages -->
									<p v-if="message.role === 'user' && message.attachments && message.attachments.length > 0" class="attachments-indicator">
										{{ message.attachments.length }} {{ message.attachments.length === 1 ? getString('image_attached') : getString('images_attached') }}
									</p>
									<!-- Section indicator for user messages -->
									<p v-if="message.role === 'user' && message.other_info && message.other_info.section_id" class="attachments-indicator">
										{{ getString('editing_section', [message.other_info.section_id.replace('section-', '')]) }}
									</p>
								</div>

								<!-- Pending/Thinking state -->
								<div v-else-if="message.status === 'pending'" class="thinking-state">
									<AppLoader :size="16" />
									<p class="thinking-text">{{ isLoading === 'loading' ? getString('loading') : getString('thinking') }}...</p>
								</div>

								<!-- Error states -->
								<div v-else-if="['failed', 'cancelled', 'incomplete'].includes(message.status)" class="error-state">
									<p class="error-text">{{ getString('ai_response_error') }}</p>
								</div>
							</div>
						</div>
						<div
							v-if="canRollback()"
							class="rollback-btn"
							@click="handleRollback"
						>
							<div class="icon-reset" v-html="getIcon('iconReset')"></div>
							{{ getString('rollback_changes') }}
						</div>
					</div>
				</div>

				<div class="chat-input-wrapper">
					<NewsletterAiInput
						v-if="newsletterStatus === 'draft'"
						:value="messageInput"
						:placeholder="getString('continue_editing_email')"
						:loading="!!isLoading"
						:attachment-count="newsletterStore.attachedImages.length"
						:thinking-mode="newsletterStore.thinkingMode"
						@update:value="messageInput = $event"
						@update:thinking-mode="newsletterStore.thinkingMode = $event"
						@send="sendMessage"
						@add-attachment="addAttachment"
					/>
					<div v-else class="newsletter-status-message">
						<div v-if="newsletterStatus === 'in_review'" class="status-content">
							<AppLoader :size="20" />
							<p>{{ getString('newsletter_being_reviewed') }}</p>
						</div>
						<div v-else-if="newsletterStatus === 'sending'" class="status-content">
							<AppLoader :size="20" />
							<p>{{ getString('newsletter_being_sent') }}</p>
						</div>
						<div v-else-if="newsletterStatus === 'scheduled'" class="status-content">
							<p>{{ getString('newsletter_scheduled_for_date', [formatScheduledDate()]) }}</p>
						</div>
						<div v-else-if="newsletterStatus === 'sent'" class="status-content sent">
							<p>{{ getString('newsletter_sent_successfully') }}</p>
							<AppButton
								:text="getString('view_stats')"
								:outline="true"
								@click="openStatsBottomSheet"
							/>
						</div>
					</div>
				</div>
			</div>

			<!-- Mobile Preview Button -->
			<div class="mobile-preview-btn-wrapper">
				<AppButton
					:text="getString('preview')"
					:outline="true"
					@click="showMobilePreview = !showMobilePreview"
				/>
			</div>

			<!-- Right Column: Email Preview -->
			<div class="preview-column" v-if="!showMobilePreview">
				<div class="preview-content" ref="previewContent">
					<div class="email-preview" :class="{loading: !compiledHtml}">
						<div v-if="compiledHtml" class="email-preview-wrapper" :style="previewWrapperStyle">
							<iframe class="email-preview-iframe" :srcdoc="compiledHtml" :style="iframeStyle"></iframe>
						</div>
						<div v-else id="newsletter-preview-loading-anim" class="preview-loading-anim"></div>
					</div>
				</div>
			</div>
		</div>

		<!-- Mobile Preview Bottom Sheet -->
		<AppBottomSheet forceWidth="100%" :active="showMobilePreview" @toggling-popup="togglePreviewBottomSheet">
			<div class="mobile-preview-wrapper" v-if="showMobilePreview">
				<ModalHeader
					:title="getString('preview')">
					<template #extra-buttons>
						<AppSwitch
							fixed-color
							left-icon="iconMobile"
							right-icon="iconDesktop"
							:checked="newsletterStore.previewMode === 'desktop'"
							@switched="togglePreviewMode"
						/>
					</template>
				</ModalHeader>
				<div id="newsletter-mobile-preview-content" class="mobile-preview-content" ref="mobilePreviewContent">
					<div class="email-preview" :class="{loading: !compiledHtml}">
						<div v-if="compiledHtml" class="email-preview-wrapper" :style="previewWrapperStyle">
							<iframe class="email-preview-iframe" :srcdoc="compiledHtml" :style="iframeStyle"></iframe>
						</div>
						<div v-else class="preview-loading-anim">
							<AppLoader :size="40" />
						</div>
					</div>
				</div>
			</div>
		</AppBottomSheet>

		<!-- Newsletter Stats Bottom Sheet -->
		<AppBottomSheet :forceWidth="statsBottomSheetWidth" :active="showStatsBottomSheet" @toggling-popup="showStatsBottomSheet = $event">
			<div class="stats-wrapper" v-if="showStatsBottomSheet">
				<ModalHeader :title="getString('view_stats')" />
				<div class="stats-content">
					<AppLoader v-if="statsLoading" :size="40" />
					<div v-else-if="statsData" class="stats-grid">
						<div class="stat-card">
							<div class="stat-value">{{ statsData.sent }}</div>
							<div class="stat-label">{{ getString('newsletter_stats_sent') }}</div>
						</div>
						<div class="stat-card">
							<div class="stat-value">{{ statsData.viewed_pct }}%</div>
							<div class="stat-label">{{ getString('newsletter_stats_delivered') }}</div>
							<div class="stat-sublabel">{{ statsData.viewed_count }} / {{ statsData.sent }}</div>
						</div>
						<div class="stat-card">
							<div class="stat-value">{{ statsData.clicked_pct }}%</div>
							<div class="stat-label">{{ getString('newsletter_stats_clicked') }}</div>
							<div class="stat-sublabel">{{ statsData.clicked_count }} / {{ statsData.sent }}</div>
						</div>
						<div class="stat-card">
							<div class="stat-value">{{ statsData.bounced }}</div>
							<div class="stat-label">{{ getString('newsletter_stats_bounced') }}</div>
						</div>
					</div>
					<p v-else class="stats-error">{{ getString('newsletter_stats_error') }}</p>
				</div>
			</div>
		</AppBottomSheet>

		<!-- Recharge Bottom Sheet -->
		<RechargeBottomSheet
			:active="showRechargeBottomSheet"
			@close="handleRechargeClose"
		/>
	</div>
</template>

<script>
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import RechargeBottomSheet from "@/console-new/components/shared/RechargeBottomSheet.vue";
import NewsletterAiInput from "@/console-new/components/marketing/NewsletterAiInput.vue";
import {useLanguageStore, useNotificationStore, useSvgStore, useCurrencyStore} from "@/shared/stores";
import {useNewsletterStore, useUserStore, useAblyStore} from "@/console-new/stores";
import mjml2html from "mjml-browser";
import API from "@/shared/helpers/API.js";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let notificationStore = useNotificationStore();
		let svgStore = useSvgStore();
		let currencyStore = useCurrencyStore();
		let newsletterStore = useNewsletterStore();
		let userStore = useUserStore();
		let ablyStore = useAblyStore();
		return {
			languageStore,
			getString: languageStore.getString,
			showNotification: notificationStore.showNotification,
			getIcon: svgStore.get,
			currencyStore,
			newsletterStore,
			userStore,
			ablyStore
		};
	},
	name: "NewsletterEdit",
	mixins: [WindowWidth],
	emits: ["add-attachment"],
	components: {
		AppChevronIcon,
		AppLoader,
		AppButton,
		AppBottomSheet,
		AppSwitch,
		ModalHeader,
		RechargeBottomSheet,
		NewsletterAiInput
	},
	data() {
		return {
			messageInput: '',
			isLoading: null, // null | 'loading' | 'thinking'
			compiledHtml: '',
			previewScrollPosition: 0,
			loadedAnimation: false,
			generationAnimation: null,
			previewScale: 1,
			availableWidth: 800,
			showMobilePreview: false,
			showStatsBottomSheet: false,
			statsData: null,
			statsLoading: false,
			showRechargeBottomSheet: false
		}
	},
	computed: {
		currentNewsletter() {
			if (!this.newsletterStore.editingNewsletterId || !this.newsletterStore.businessNewsletters) {
				return null;
			}
			return this.newsletterStore.businessNewsletters.find(
				n => n.business_newsletter_id === this.newsletterStore.editingNewsletterId
			);
		},
		currentNewsletterVersion() {
			// Computed property that returns the version string to watch for changes
			return this.currentNewsletter?.draft_version || this.currentNewsletter?.current_version || null;
		},
		newsletterStatus() {
			return this.currentNewsletter?.status || 'draft';
		},
		statsBottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		iframeWidth() {
			if (this.newsletterStore.previewMode === 'mobile') {
				// Mobile mode: always use 375px
				return 375;
			}
			// Desktop mode: use available width if larger than 800px, otherwise 800px minimum
			return Math.max(800, this.availableWidth);
		},
		previewWrapperStyle() {
			return {
				transform: `scale(${this.previewScale})`,
				transformOrigin: 'center top'
			};
		},
		iframeStyle() {
			// Compensate height for the scale transform applied to the wrapper
			const heightCompensation = this.previewScale > 0 ? (100 / this.previewScale) : 100;
			return {
				width: `${this.iframeWidth}px`,
				height: `${heightCompensation}%`
			};
		},
		getMessageClass() {
			return (message) => {
				if (message.role === 'system' || (message.role === 'user' && message.type === 'newsletter_rollback')) {
					return 'system-message';
				}
				return message.role === 'user' ? 'user-message' : 'ai-message';
			};
		},
		getImageGenerationLabel() {
			return (message) => {
				if (!message.images || message.images.length === 0) {
					return '';
				}

				const imageCount = message.images.length;
				const completedCount = message.images.filter(img => img.status === 'completed').length;
				const pendingCount = message.images.filter(img => img.status === 'pending').length;
				const failedCount = message.images.filter(img => ['failed', 'cancelled', 'incomplete'].includes(img.status)).length;

				// All completed
				if (completedCount === imageCount) {
					return imageCount === 1
						? this.getString('generated_1_image')
						: this.getString('generated_x_images', [imageCount]);
				}

				// All failed
				if (failedCount === imageCount) {
					return imageCount === 1
						? this.getString('failed_to_generate_1_image')
						: this.getString('failed_to_generate_x_images', [imageCount]);
				}

				// Some pending
				if (pendingCount > 0) {
					return pendingCount === 1
						? this.getString('generating_1_image')
						: this.getString('generating_x_images', [pendingCount]);
				}

				// Mixed states (some completed, some failed)
				return imageCount === 1
					? this.getString('generated_1_image')
					: this.getString('generated_x_images', [completedCount]);
			};
		}
	},
	methods: {
		formatDuration(createdAt, finalizedAt) {
			if (!createdAt || !finalizedAt) {
				return '';
			}
			const start = new Date(createdAt);
			const end = new Date(finalizedAt);
			const durationMs = end - start;
			const durationSeconds = Math.round(durationMs / 1000);

			if (durationSeconds < 60) {
				return `${durationSeconds}s`;
			}
			const minutes = Math.floor(durationSeconds / 60);
			const seconds = durationSeconds % 60;
			return `${minutes}m ${seconds}s`;
		},
		formatChargedAmount(amount) {
			if (amount === null || amount === undefined) {
				return '';
			}
			// Amount is in cents, use currencyStore to format (use absolute value)
			return this.currencyStore.readableCurrencyValue(Math.abs(amount));
		},
		formatScheduledDate() {
			if (!this.currentNewsletter?.scheduled_at) {
				return '';
			}
			const date = new Date(this.currentNewsletter.scheduled_at);
			return date.toLocaleString(this.languageStore.lang === 'it' ? 'it-IT' : 'en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			});
		},
		calculatePreviewScale() {
			this.$nextTick(() => {
				let container;
				let padding;

				// For mobile preview bottom sheet (teleported, so use ID)
				if (this.showMobilePreview) {
					container = document.getElementById('newsletter-mobile-preview-content');
					padding = 0; // 10px * 2
				} else {
					// For desktop preview
					container = this.$refs.previewContent;
					// Determine padding based on screen width
					padding = window.innerWidth <= 1100 ? 20 : 40; // 10px * 2 or 20px * 2
				}

				if (!container) return;

				const availableWidth = container.clientWidth - padding;
				this.availableWidth = availableWidth;
				const scale = Math.min(1, availableWidth / this.iframeWidth);
				this.previewScale = scale;
			});
		},
		handleResize() {
			// Close mobile bottom sheet if window width exceeds 850px
			if (this.showMobilePreview && window.innerWidth > 850) {
				this.showMobilePreview = false;
			}
			this.calculatePreviewScale();
		},
		hasGeneratingImages(message) {
			if (!message.images || message.images.length === 0) {
				return false;
			}
			return message.images.some(img => img.status === 'pending');
		},
		closeEditor() {
			this.newsletterStore.unsubscribeFromNewsletterUpdates();
			this.newsletterStore.editingNewsletterId = null;
			this.newsletterStore.conversationMessages = [];
			this.selectedSectionId = null;
		},
		addAttachment() {
			this.$emit('add-attachment');
		},
		togglePreviewBottomSheet(sheetState) {
			this.showMobilePreview = sheetState;
		},
		togglePreviewMode(isDesktop) {
			this.newsletterStore.previewMode = isDesktop ? 'desktop' : 'mobile';
		},
		setupAblySubscription() {
			if (this.newsletterStore.editingNewsletterId) {
				this.ablyStore.startAbly(() => {
					this.newsletterStore.subscribeToNewsletterUpdates(this.newsletterStore.editingNewsletterId);
				});
			}
		},
		fetchConversation() {
			if (this.newsletterStore.editingNewsletterId) {
				this.newsletterStore.getNewsletterConversation(this.newsletterStore.editingNewsletterId);
			}
		},
		scrollToBottom() {
			this.$nextTick(() => {
				const chatMessages = this.$refs.chatMessages;
				if (chatMessages) {
					chatMessages.scrollTop = chatMessages.scrollHeight;
				}
			});
		},
		sendMessage() {
			if(this.isLoading || !this.messageInput?.trim()) {
				if(this.isLoading) {
					this.showNotification('newsletter-pending');
				}
				return;
			}

			const prompt = this.messageInput.trim();
			const images = [...this.newsletterStore.attachedImages];
			const sectionId = this.newsletterStore.selectedSectionId;
			this.isLoading = 'loading';

			// Add the user message optimistically to the conversation
			const userMessage = {
				role: 'user',
				content: prompt,
				status: 'completed',
				attachments: images
			};
			if (sectionId) {
				userMessage.other_info = { section_id: sectionId };
			}
			this.newsletterStore.conversationMessages.push(userMessage);

			// Add a pending AI response
			this.newsletterStore.conversationMessages.push({
				role: 'assistant',
				content: null,
				status: 'pending'
			});

			this.messageInput = '';
			this.newsletterStore.attachedImages = [];

			const selectionId = this.newsletterStore.selectedSectionId;

			this.newsletterStore.selectedSectionId = null;
			this.highlightSelectedSection();

			API.init().editNewsletter(
				this.userStore.currBusinessId,
				this.newsletterStore.editingNewsletterId,
				prompt,
				selectionId,
				images,
				this.newsletterStore.thinkingMode,
				response => {
					if (response && response.result === "OK") {
						this.isLoading = 'thinking';
						// Clear the section selection after successful send
						// Webhook will handle updating the newsletter
						// The conversation will be updated via Ably or page refresh
					} else {
						// Show specific notification based on error type
						if (response && response.details) {
							switch (response.details) {
								case 'newsletter_pending':
									this.showNotification('newsletter-pending');
									break;
								case 'max_images_exceeded':
									this.showNotification('newsletter-max-images');
									break;
								case 'newsletter_not_in_draft':
									this.showNotification('newsletter-not-draft');
									break;
								case 'insufficient_credits':
									this.showRechargeBottomSheet = true;
									this.showNotification('insufficient-credits-for-newsletter');
									break;
								default:
									this.showNotification('generic-error');
							}
						} else {
							this.showNotification('generic-error');
						}
						// Remove the optimistic messages on error
						this.newsletterStore.conversationMessages.pop();
						this.newsletterStore.conversationMessages.pop();
						// Restore the user's text and images only if the textarea is still empty
						if (!this.messageInput || this.messageInput.trim() === '') {
							this.messageInput = prompt;
							this.newsletterStore.attachedImages = images;
						}
						this.isLoading = null;
					}
				}
			);
		},
		savePreviewScrollPosition() {
			const iframe = this.$el?.querySelector('.email-preview-iframe');
			if (iframe && iframe.contentWindow) {
				try {
					this.previewScrollPosition = iframe.contentWindow.scrollY || 0;
				} catch (e) {
					// If we can't access the iframe (cross-origin), ignore
					this.previewScrollPosition = 0;
				}
			}
		},
		restorePreviewScrollPosition() {
			const iframe = this.$el?.querySelector('.email-preview-iframe');
			if (iframe && iframe.contentWindow && this.previewScrollPosition > 0) {
				try {
					iframe.contentWindow.scrollTo(0, this.previewScrollPosition);
				} catch (e) {
					// If we can't access the iframe (cross-origin), ignore
				}
			}
		},
		loadPreviewAnimation() {
			if(typeof bodymovin !== 'undefined') {
				const animationPath = 'https://img.qromo.io/img/vue/anim/ai-newsletter' + (this.userStore.darkMode ? '-dark' : '-light') + '.json';
				if(!this.loadedAnimation) {
					this.generationAnimation = bodymovin.loadAnimation({
						container: document.getElementById('newsletter-preview-loading-anim'),
						renderer: 'svg',
						loop: true,
						autoplay: true,
						path: animationPath,
						rendererSettings: {
							preserveAspectRatio: 'xMidYMid meet'
						}
					});
					this.loadedAnimation = true;
				}
			}
		},
		compileEmailPreview() {
			const version = this.currentNewsletter?.draft_version || this.currentNewsletter?.current_version;
			if(version) {
				// Save current scroll position before updating
				this.savePreviewScrollPosition();

				const result = mjml2html(version, {
					validationLevel: 'skip',
					minify: true
				});
				this.compiledHtml = result.html;

				// Restore scroll position after iframe loads
				this.$nextTick(() => {
					const iframe = this.$el?.querySelector('.email-preview-iframe');
					if (iframe) {
						iframe.addEventListener('load', () => {
							this.restorePreviewScrollPosition();
							this.setupIframeClickListener();
						}, { once: true });
					}
				});
			} else {
				// No version yet, show loading animation
				this.$nextTick(() => {
					this.loadPreviewAnimation();
				});
			}
		},
		setupIframeClickListener() {
			const iframe = this.$el?.querySelector('.email-preview-iframe');
			if (!iframe || !iframe.contentWindow) {
				return;
			}

			try {
				iframe.contentWindow.document.addEventListener('click', this.handleIframeClick);
				this.injectImageLoadingAnimation();
			} catch (e) {
				console.error('Could not attach click listener to iframe:', e);
			}
		},
		handleIframeClick(event) {
			let element = event.target;

			// Traverse up the DOM tree to find a parent with a class starting with 'section-'
			while (element && element.tagName !== 'BODY' && element.tagName !== 'HTML') {
				if (element.classList) {
					for (let className of element.classList) {
						if (className.startsWith('section-')) {
							// Found a section element - toggle if same section clicked
							if (this.newsletterStore.selectedSectionId === className) {
								this.newsletterStore.selectedSectionId = null;
							} else {
								this.newsletterStore.selectedSectionId = className;
							}
							this.highlightSelectedSection();
							return;
						}
					}
				}
				element = element.parentElement;
			}

			// If we didn't find a section, clear the selection
			this.newsletterStore.selectedSectionId = null;
			this.highlightSelectedSection();
		},
		highlightSelectedSection() {
			const iframe = this.$el?.querySelector('.email-preview-iframe');
			if (!iframe || !iframe.contentWindow) {
				return;
			}

			try {
				const iframeDoc = iframe.contentWindow.document;

				// Inject highlight CSS if not already present
				if (!iframeDoc.getElementById('qromo-section-highlight-style')) {
					const style = iframeDoc.createElement('style');
					style.id = 'qromo-section-highlight-style';
					style.textContent = `
						.qromo-selected-section {
							position: relative !important;
						}
						.qromo-selected-section::after {
							content: '' !important;
							position: absolute !important;
							top: 0 !important;
							left: 0 !important;
							right: 0 !important;
							bottom: 0 !important;
							background: rgba(0, 0, 0, 0.35) !important;
							border: 2px solid rgba(35, 82, 162, 0.8) !important;
							pointer-events: none !important;
							z-index: 9999 !important;
						}
					`;
					iframeDoc.head.appendChild(style);
				}

				// Remove highlight class from all elements
				const previouslySelected = iframeDoc.querySelectorAll('.qromo-selected-section');
				previouslySelected.forEach(el => el.classList.remove('qromo-selected-section'));

				// Add highlight class to selected section
				if (this.newsletterStore.selectedSectionId) {
					const selectedElement = iframeDoc.querySelector('.' + this.newsletterStore.selectedSectionId);
					if (selectedElement) {
						selectedElement.classList.add('qromo-selected-section');
					}
				}
			} catch (e) {
				console.error('Could not highlight section:', e);
			}
		},
		injectImageLoadingAnimation() {
			const iframe = this.$el?.querySelector('.email-preview-iframe');
			if (!iframe || !iframe.contentWindow) {
				console.log('[Newsletter] No iframe or iframe.contentWindow found');
				return;
			}

			try {
				const iframeDoc = iframe.contentWindow.document;

				// Inject loading animation CSS if not already present
				if (!iframeDoc.getElementById('qromo-image-loading-style')) {
					const style = iframeDoc.createElement('style');
					style.id = 'qromo-image-loading-style';
					style.textContent = `
						@keyframes qromo-placeholder-pulse {
							0%, 100% { opacity: 0.7; }
							50% { opacity: 1; }
						}
						.qromo-loading-placeholder {
							animation: qromo-placeholder-pulse 1.5s ease-in-out infinite !important;
						}
					`;
					iframeDoc.head.appendChild(style);
				} else {
					console.log('[Newsletter] Animation CSS already present');
				}

				// Find all images with placeholder URLs and add the loading class
				const images = iframeDoc.querySelectorAll('img');

				let placeholderCount = 0;
				images.forEach(img => {
					const src = img.getAttribute('src') || '';
					if (src.includes('/img/placeholders/mountain.php') || src.includes('/img/placeholders/pattern.php')) {
						img.classList.add('qromo-loading-placeholder');
						placeholderCount++;
					}
				});
			} catch (e) {
				console.error('[Newsletter] Could not inject image loading animation:', e);
			}
		},
		canRollback() {
			const messagesNoSystem = this.newsletterStore.conversationMessages.filter(msg => msg.role !== "system");
			if (messagesNoSystem.length === 0) return false;
			const lastMessage = messagesNoSystem[messagesNoSystem.length - 1];
			return lastMessage.role === 'assistant' &&
			       lastMessage.status === 'completed' &&
			       lastMessage.type === 'newsletter_edit';
		},
		handleRollback() {
			if (this.isLoading || !this.newsletterStore.editingNewsletterId) {
				return;
			}

			this.isLoading = 'loading';

			// Add optimistic rollback system message
			this.newsletterStore.conversationMessages.push({
				role: 'user',
				content: '__rollback__',
				status: 'completed',
				type: 'newsletter_rollback'
			});

			// Add pending AI response
			this.newsletterStore.conversationMessages.push({
				role: 'assistant',
				content: null,
				status: 'pending',
				type: 'newsletter_rollback'
			});

			this.newsletterStore.rollbackNewsletter(this.newsletterStore.editingNewsletterId, response => {
				if (response && response.result === 'OK') {
					this.isLoading = 'thinking';
					// isLoading will be cleared when conversation updates with rollback response
				} else {
					this.isLoading = null;
					if (response && response.details === 'insufficient_credits') {
						this.showRechargeBottomSheet = true;
						this.showNotification('insufficient-credits-for-newsletter');
					} else {
						this.showNotification('generic-fail');
					}
					// Remove the optimistic messages on error
					this.newsletterStore.conversationMessages.pop();
					this.newsletterStore.conversationMessages.pop();
				}
			});
		},
		handleRechargeClose(success) {
			this.showRechargeBottomSheet = false;
			if (success) {
				this.userStore.refreshBusiness(this.userStore.currBusinessId);
			}
		},
		openStatsBottomSheet() {
			this.showStatsBottomSheet = true;
			this.statsLoading = true;
			this.statsData = null;

			API.init().getNewsletterStats(
				this.userStore.currBusinessId,
				this.newsletterStore.editingNewsletterId,
				response => {
					this.statsLoading = false;
					if (response && response.result === "OK" && response.stats) {
						this.statsData = response.stats;
					} else {
						this.showNotification('generic-error');
					}
				}
			);
		}
	},
	mounted() {
		this.compileEmailPreview();
		if (this.newsletterStore.editingNewsletterId) {
			this.fetchConversation();
			this.setupAblySubscription();
		}
		this.calculatePreviewScale();
		window.addEventListener('resize', this.handleResize);
	},
	beforeUnmount() {
		this.newsletterStore.unsubscribeFromNewsletterUpdates();
		if(this.generationAnimation) {
			this.generationAnimation.destroy();
		}
		window.removeEventListener('resize', this.handleResize);
	},
	watch: {
		'newsletterStore.editingNewsletterId'(newId, oldId) {
			if (oldId) {
				// Unsubscribe from old newsletter
				this.newsletterStore.unsubscribeFromNewsletterUpdates();
			}
			if (newId) {
				this.fetchConversation();
				this.setupAblySubscription();
			}
		},
		'newsletterStore.conversationMessages': {
			handler(newMessages) {
				this.scrollToBottom();

				// Update isLoading state based on last message
				if (newMessages && newMessages.length > 0) {
					const lastMessage = newMessages[newMessages.length - 1];
					if (lastMessage.role === 'assistant') {
						if (lastMessage.status === 'completed') {
							this.isLoading = null;
						} else if (lastMessage.status === 'pending') {
							// Keep thinking state if already loading
							if (this.isLoading) {
								this.isLoading = 'thinking';
							}
						} else if (['failed', 'cancelled', 'incomplete'].includes(lastMessage.status)) {
							this.isLoading = null;
						}
					}
				}
			},
			deep: true
		},
		currentNewsletterVersion(newVal, oldVal) {
			if (newVal && newVal !== oldVal) {
				this.compileEmailPreview();
			}
		},
		'newsletterStore.previewMode'() {
			this.calculatePreviewScale();
		},
		showMobilePreview(newVal) {
			if (newVal) {
				// When bottom sheet opens, recalculate scale after it's rendered
				this.$nextTick(() => {
					this.calculatePreviewScale();
				});
			}
		}
	},
}
</script>

<style scoped lang="scss">
.editing-view {
	width: 100%;
	height: 100%;
	display: flex;
	justify-content: center;
}

.edit-container {
	display: flex;
	width: 100%;
	height: 100%;
	gap: 20px;

	@media (max-width: 1100px) {
		gap: 10px;
	}

	@media (max-width: 850px) {
		flex-direction: column;
		align-items: center;
		gap: 10px;
	}
}

.chat-column {
	flex: 0 0 500px;
	display: flex;
	flex-direction: column;
	background: var(--color-bg-primary-2);
	border-radius: 13px;
	overflow: hidden;

	@media (max-width: 1250px) {
		flex: 0 0 420px;
	}

	@media (max-width: 1100px) {
		flex: 0 0 380px;
	}

	@media (max-width: 850px) {
		flex: 1 1 0;
		width: 100%;
		max-width: 540px;
		min-height: 0;
	}
}

.chat-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 20px;
	background: var(--bg-0);
	border: 1px solid var(--color-border-2);
	border-radius: 13px 13px 0 0;
	cursor: pointer;

	@media (max-width: 1100px) {
		padding: 10px;
	}

	.back-btn {
		background: none;
		border: none;
		padding: 4px;
		color: var(--color-text-2);
		transition: color 0.2s;
		margin-right: 8px;
		cursor: pointer;

		&:hover {
			color: var(--color-text-1);
		}
	}

	.chat-title {
		font-weight: 600;
		font-size: 18px;
		color: var(--txt-0);
		margin: 0;
		flex: 1;
		@include truncateString(1);

		@media (max-width: 1100px) {
			font-size: 16px;
		}
	}

	.close-btn {
		background: none;
		border: none;
		cursor: pointer;
		padding: 8px;
		color: var(--color-text-2);
		transition: color 0.2s;
		margin-left: 12px;

		&:hover {
			color: var(--color-text-1);
		}
	}
}

.chat-messages {
	flex: 1;
	overflow-y: auto;
	padding: 20px;

	@media (max-width: 1100px) {
		padding: 10px;
	}
}

.messages-list {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-start;
	gap: 16px;

	.rollback-btn {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: transparent;
		color: var(--color-txt-1);
		font-size: 13px;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.2s;
		opacity: 0.5;

		.icon-reset {
			width: 14px;
			height: 14px;
			display: flex;
			align-items: center;
			justify-content: center;

			:deep(svg) {
				width: 14px;
				height: 14px;
				fill: currentColor;
			}
		}

		&:hover:not(:disabled) {
			opacity: 1;
			color: var(--color-txt-1);
		}

		&:disabled {
			opacity: 0.3;
			cursor: not-allowed;
		}
	}
}

.no-messages {
	text-align: center;
	padding: 20px;

	p {
		font-size: 18px;
	}

}

.message {
	display: flex;
	width: 100%;

	.message-content {
		width: 80%;
		padding: 12px 16px;
		border-radius: 13px;
		user-select: text;

		p {
			margin: 0;
			color: var(--color-txt-1);
			font-size: 14px;
			line-height: 1.5;
			user-select: text;

			white-space: pre-wrap;
			overflow: hidden;
			text-overflow: ellipsis;
			width: 100%;
		}

		.ai-message-wrapper {
			width: 100%;
			display: flex;
			flex-direction: column;
			align-items: flex-start;
		}

		.ai-response {
			margin: 0;
			color: var(--color-txt-1);
			font-size: 14px;
			line-height: 1.5;
			white-space: pre-wrap;
			word-wrap: break-word;
			font-family: inherit;
		}

		.ai-message-meta {
			display: flex;
			justify-content: space-between;
			align-items: center;
			margin-top: 8px;
			width: 100%;
			gap: 12px;

			p {
				margin: 0;
				font-size: 11px;
				color: var(--color-txt-2);
				opacity: 0.7;
			}

			.meta-duration {
				text-align: left;
			}

			.meta-cost {
				text-align: right;
			}
		}

		.rollback-btn {
			display: inline-flex;
			align-items: center;
			gap: 6px;
			margin-top: 12px;
			margin-left: auto;
			margin-right: auto;
			padding: 6px 12px;
			background: transparent;
			color: var(--color-txt-1);
			font-size: 13px;
			font-weight: 500;
			cursor: pointer;
			transition: all 0.2s;
			opacity: 0.5;

			.icon-reset {
				width: 14px;
				height: 14px;
				display: flex;
				align-items: center;
				justify-content: center;

				:deep(svg) {
					width: 14px;
					height: 14px;
					fill: currentColor;
				}
			}

			&:hover:not(:disabled) {
				background: var(--color-primary-alpha-2);
				border-color: var(--color-border-1);
				color: var(--color-txt-1);
			}

			&:disabled {
				opacity: 0.5;
				cursor: not-allowed;
			}
		}

		.thinking-state {
			display: flex;
			align-items: flex-start;
			width: 100%;
			gap: 8px;

			.thinking-text {
				color: var(--color-text-2);
				font-size: 14px;
				font-style: italic;
			}
		}

		.error-state {
			.error-text {
				color: var(--color-danger);
				font-size: 14px;
			}
		}
	}

	&.user-message {
		justify-content: flex-end;

		.message-content {
			background: var(--color-bg-primary-1);
			border-top-right-radius: 0;
			max-width: 80%;
			width: auto;

			p {
				color: var(--color-txt);
			}

			.attachments-indicator {
				margin-top: 4px;
				font-size: 12px;
				color: var(--color-txt-2);
				opacity: 0.7;
			}
		}
	}

	&.ai-message {
		justify-content: flex-start;

		.message-content {
			padding: 0 ;
			background: transparent;
			max-width: 100%;
			width: 100%;
		}
	}
}

.chat-input-wrapper {
	padding: 20px;
	border-top: 1px solid var(--color-border-2);
	display: flex;
	justify-content: center;

	@media (max-width: 1100px) {
		padding: 10px;
	}
}

/* RIGHT COLUMN: PREVIEW */
.preview-column {
	flex: 1;
	display: flex;
	flex-direction: column;
	background: var(--color-bg-primary-2);
	border-radius: 13px;
	overflow: hidden;

	@media (max-width: 850px) {
		display: none;
	}
}

.preview-tabs {
	display: flex;
	gap: 8px;

	.tab-btn {
		background: transparent;
		border: none;
		padding: 8px 16px;
		border-radius: 8px;
		cursor: pointer;
		font-size: 14px;
		font-weight: 500;
		color: var(--color-text-2);
		transition: all 0.2s;

		&:hover {
			background: var(--color-bg-primary-3);
			color: var(--color-text-1);
		}

		&.active {
			background: var(--color-primary);
			color: white;
		}
	}
}

.preview-actions {
	display: flex;
	gap: 12px;

	.action-btn {
		background: transparent;
		border: 1px solid var(--color-border-1);
		padding: 10px 20px;
		border-radius: 8px;
		cursor: pointer;
		font-size: 14px;
		font-weight: 500;
		color: var(--color-text-1);
		transition: all 0.2s;

		&:hover {
			background: var(--color-bg-primary-3);
		}

		&.primary {
			background: var(--color-primary);
			border-color: var(--color-primary);
			color: white;

			&:hover {
				opacity: 0.9;
			}
		}
	}
}

.preview-content {
	flex: 1;
	overflow-y: auto;
	padding: 20px;
	box-shadow: inset 0 1px 4px var(--color-box-shadow-3);

	@media (max-width: 1100px) {
		padding: 10px;
	}
}

.email-preview {
	background: var(--color-bg-primary-1);
	border-radius: 8px;
	min-height: 100%;
	height: 100%;
	box-shadow: inset 0 1px 4px var(--color-box-shadow-1);
	position: relative;
	border-radius: 13px;
	overflow: hidden;
	display: flex;
	justify-content: center;
	align-items: flex-start;

	.email-preview-wrapper {
		position: relative;
		transition: transform 0.3s ease;
		width: fit-content;
		height: 100%;
		display: flex;
		align-items: flex-start;

		.email-preview-iframe {
			border: none;
			display: block;
			height: 100%;
		}
	}

	.preview-loading-anim {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		max-width: 80%;
		max-height: 80%;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&.loading {
		background: var(--color-bg-primary-1);
	}
}

.email-subject {
	margin-bottom: 24px;
	padding-bottom: 16px;
	border-bottom: 1px solid var(--color-border-1);

	.subject-label {
		font-size: 12px;
		font-weight: 600;
		color: var(--color-txt-2);
		margin: 0 0 8px 0;
		text-transform: uppercase;
	}

	.subject-text {
		font-size: 18px;
		font-weight: 600;
		color: var(--color-txt);
		margin: 0;
	}
}

.email-body {
	color: var(--color-txt);
	line-height: 1.6;

	p {
		margin: 0;
	}
}

.rollback-label {
	text-align: center;
	color: var(--color-danger);
	font-size: 14px;
	font-weight: 500;
	padding: 8px 16px;
	width: 100%;
}

.image-generation-label {
	display: flex;
	align-items: baseline;
	justify-content: flex-start;
	gap: 8px;
	width: 100%;
	font-style: italic;

	span {
		color: var(--color-txt-2);
		font-size: 13px;
		font-weight: 500;
	}

	.image-cost {
		opacity: 0.7;
		font-weight: 400;
		font-size: 11px;
		color: var(--color-txt-2);
	}
}

.message.system-message {
	justify-content: center;

	.message-content {
		background: transparent;
		max-width: 100%;
		width: 100%;
		padding: 0;
	}
}

.mobile-preview-btn-wrapper {
	display: none;

	@media (max-width: 850px) {
		display: block;
		flex: 0 0 auto;
		width: 100%;
		max-width: 540px;
	}

	:deep(.base-button) {
		width: 100%;
		max-width: none;
		height: 40px;
	}
}

.mobile-preview-content {
	width: 100%;
	height: calc(92vh - 130px);
	overflow-y: auto;
	box-sizing: border-box;

	.email-preview {
		background: var(--color-bg-primary-1);
		border-radius: 8px;
		min-height: 100%;
		height: 100%;
		box-shadow: inset 0 1px 4px var(--color-box-shadow-1);
		position: relative;
		border-radius: 13px;
		overflow: hidden;
		display: flex;
		justify-content: center;
		align-items: flex-start;

		.email-preview-wrapper {
			position: relative;
			transition: transform 0.3s ease;
			width: fit-content;
			height: 100%;
			display: flex;
			align-items: flex-start;

			.email-preview-iframe {
				border: none;
				display: block;
				height: 100%;
			}
		}

		.preview-loading-anim {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			display: flex;
			align-items: center;
			justify-content: center;
		}

		&.loading {
			background: var(--color-bg-primary-1);
		}
	}
}

.switch-icons {
	display: flex;
	align-items: center;
	gap: 8px;
	order: -1;
}

.newsletter-status-message {
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 8px;

	@media (max-width: 1100px) {
		padding: 10px;
	}

	.status-content {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
		text-align: center;

		p {
			margin: 0;
			font-size: 14px;
			font-weight: 500;
		}

		&.sent {
			gap: 16px;

			:deep(.base-button) {
				width: auto;
				min-width: 150px;
			}
		}
	}
}

.stats-wrapper {
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;

	.stats-content {
		flex: 1;
		padding: 20px;
		overflow-y: auto;
		display: flex;
		align-items: center;
		justify-content: center;

		.stats-error {
			color: var(--color-danger);
			font-size: 14px;
			text-align: center;
		}

		.stats-grid {
			display: grid;
			grid-template-columns: repeat(4, 1fr);
			gap: 20px;
			width: 100%;
			max-width: 1000px;

			@media (max-width: 768px) {
				grid-template-columns: repeat(2, 1fr);
				gap: 15px;
			}

			@media (max-width: 480px) {
				grid-template-columns: 1fr;
			}

			.stat-card {
				background: var(--color-bg-primary-1);
				border: 1px solid var(--color-border-2);
				border-radius: 13px;
				padding: 24px;
				display: flex;
				flex-direction: column;
				align-items: center;
				text-align: center;
				transition: all 0.2s;

				&:hover {
					border-color: var(--color-border-1);
					box-shadow: 0 2px 8px var(--color-box-shadow-2);
				}

				.stat-value {
					font-size: 32px;
					font-weight: 700;
					color: var(--color-txt-0);
					margin-bottom: 8px;
					line-height: 1;
				}

				.stat-label {
					font-size: 14px;
					font-weight: 600;
					color: var(--color-txt-1);
					text-transform: uppercase;
					letter-spacing: 0.5px;
				}

				.stat-sublabel {
					font-size: 12px;
					color: var(--color-txt-2);
					margin-top: 4px;
				}
			}
		}
	}
}
</style>
